// @flow
import * as React from "react";
import PropTypes from 'prop-types';

import type Player from "../player/player.js";
import type {To} from "../player/urlresolver.js";
import {trackOutbound} from "../analytics/analytics";
import {isIE, isEdge} from '../environment.js';

const ABS_URL = /^((\w+:)?\/\/)|(mailto:)/;

type HandleParams = {
	onClick?: (ev: MouseEvent) => void;
	player: Player;
	mode?: string;
};

export function skipToNative(ev: MouseEvent) {
	// everything thats not a left mouse click
	if (ev.button !== 0) {
		return true;
	}
	// modified clicks
	if (ev.metaKey || ev.altKey || ev.ctrlKey || ev.shiftKey) {
		return true;
	}
	return false;
}

export function handleClick({onClick, player, mode = 'internal'}: HandleParams, ev: MouseEvent) {
	if (onClick) {
		onClick(ev);
	}

	// event is already canceled
	if (ev.defaultPrevented || skipToNative(ev)) {
		return;
	}
	const elem: HTMLAnchorElement = (ev.currentTarget: any);
	const href = elem.getAttribute("href");
	if (!href) {
		return;
	}
	
	// Because we generate query history urls as absolute urls we have to filter them out here and only forward the 
	// actual path & query part to the player so we don't have to reload the whole page
	if (mode !== 'native' && 
		player.options.history === 'query' &&
		~href.indexOf(`?${player.options.historyQueryParam}/`) && 
		~href.indexOf('://' + player.options.absoluteHost)
	) {
		player.navigateTo('/' + href.split(`?${player.options.historyQueryParam}/`)[1].replace('&', '?'));
		ev.preventDefault();
		return;
	} else if (mode !== 'native' && 
		player.options.history === 'hash' &&
		~href.indexOf(`#!`) && 
		~href.indexOf('://' + player.options.absoluteHost) &&
		elem.getAttribute("target") !== "_blank"
	) {
		player.navigateTo('/' + href.split(`#!/`)[1]);
		ev.preventDefault();
		return;
	}

	// ignore absolute urls or urls opening externally
	if (ABS_URL.test(href) || elem.target) {
		trackOutbound(href);
		return;
	}
	if (mode === 'native') {
		return;
	}
	player.navigateTo(href);
	ev.preventDefault();
}

type Props = {
	_ref?: (el: any) => void;
	to: ?To;
	children?: React.Element<*> | Array<React.Element<*>>;
	onClick?: (ev: MouseEvent) => void;
};

function checkInViewport(elem) {
	if (!elem.offsetParent) { return false; }
	const viewport = {
		top: 0,
		bottom: window.innerHeight,
	};
	const rect = elem.getBoundingClientRect();

	return rect.bottom >= viewport.top && rect.top <= viewport.bottom;
}

class Link extends React.Component {
	context: {player: Player};

	static contextTypes = {
		player: PropTypes.any,
	};

	props: Props;

	handleClick = (ev: MouseEvent) => {
		this.triggered = true;
		const {player} = this.context;
		const {onClick, mode} = this.props;
		handleClick({onClick, player, mode}, ev);
	};

	handleScroll = () => {
		const visible = checkInViewport(this.elem);
		if (visible && !this.triggered) {
			this.handleClick({button: 0, preventDefault: () => {}, currentTarget: this.elem});
		}
	};

	componentDidUpdate(oldProps) {
		//TODO: we might need to do some deep comparison here
		if (oldProps.to !== this.props.to) {
			this.triggered = false;
			if (!oldProps.triggerInViewport && this.props.triggerInViewport) {
				window.addEventListener('scroll', this.handleScroll);
			} else if (oldProps.triggerInViewport && !this.props.triggerInViewport) {
				window.removeEventListener('scroll', this.handleScroll);
			}
		}
	}

	componentDidMount() {
		const {triggerInViewport} = this.props;
		if (triggerInViewport) {
			window.addEventListener('scroll', this.handleScroll);
		}
	}

	componentWillUnmount() {
		window.removeEventListener('scroll', this.handleScroll);
	}

	setRef = ref => {
		const {_ref} = this.props;
		if(_ref) _ref(ref);
		this.elem = ref;
	}

	render() {
		const {_ref, children, to, title, triggerInViewport, ...props} = this.props;
		if (to) {
			props.href = this.context.player.url.create(to);
		}

		return (
			<a {...props} title={title} ref={this.setRef} onClick={this.handleClick}>{children}</a>
		);
	}
}

export default Link;



// WEBPACK FOOTER //
// ./lib/components/link.js